import { toLocale, translateFromHeadersOrCookieOrQuery } from "@mindshift/utils/datascope";
import { ref } from "vue";
import { DEFAULT_LOCALE, DOMAIN_TO_LOCALE, LOCALE_SEPARATOR, MAPPED_LOCALES } from "../constants/locales";
import { getCountryFromLocale, getLocaleConfigByCountry, getLocaleQueryParam } from "../utils/locale";

interface HeadersObject {
  [key: string]: string | string[] | undefined;
}

export type LocaleContext = {
  locale: string;
  country: string;
  language: string;
};

function getHost(headers?: HeadersObject): string | null {
  let host: string | null | undefined;

  if (headers?.host) {
    const raw = headers.host;
    host = Array.isArray(raw) ? raw[0] : raw;
  } else {
    host = globalThis.location?.host;
  }

  if (host?.includes("-ni.co.uk")) {
    return "xi";
  }

  return host || null;
}

function getTldFromHost(host: string | null): string | null {
  if (!host) return null;
  const parts = host.split(".");
  const tld = parts.at(-1)?.split(":")[0];
  return tld && DOMAIN_TO_LOCALE.has(tld) ? tld : null;
}

function formatLocaleResult(localeStr: string): LocaleContext | null {
  const mapping = MAPPED_LOCALES.get(localeStr);
  if (!mapping) return null;

  const [language, country] = mapping;
  return { locale: localeStr, country, language };
}

export function getLocaleFromDataScope(headers?: HeadersObject, serverLocaleQuery?: string) {
  const effectiveQuery = serverLocaleQuery || getLocaleQueryParam();
  const dataScope = translateFromHeadersOrCookieOrQuery(headers || {}, { locale: effectiveQuery });

  if (!dataScope) return null;

  return {
    locale: toLocale(dataScope),
    country: dataScope.country,
    language: dataScope.language,
  };
}

function getLocaleFromUrl(headers?: HeadersObject) {
  const host = getHost(headers);
  const tld = getTldFromHost(host);

  if (!tld) return null;

  const defaultLocale = DOMAIN_TO_LOCALE.get(tld);
  if (!defaultLocale) return null;

  const hostCountry = getCountryFromLocale(defaultLocale);
  const countryConfig = getLocaleConfigByCountry().get(hostCountry);

  if (!countryConfig?.isMultiLang) {
    return formatLocaleResult(defaultLocale);
  }

  if (globalThis.window) {
    const pathSegment = globalThis.window.location.pathname.split("/")[1];
    const match = pathSegment?.match(/^([a-z]{2})[_-]([A-Z]{2})$/i);

    if (match) {
      const [_, lang, country] = match;
      const potentialLocale = `${lang?.toLowerCase()}${LOCALE_SEPARATOR}${country?.toUpperCase()}`;

      if (country?.toUpperCase() === hostCountry && MAPPED_LOCALES.has(potentialLocale)) {
        return formatLocaleResult(potentialLocale);
      }
    }
  }

  return formatLocaleResult(defaultLocale);
}

function getLocaleFromWindow() {
  const win = globalThis.window;
  if (!win?.mindShiftNav?.locale) return null;

  const normalizedLocale = win.mindShiftNav.locale.replace("-", LOCALE_SEPARATOR);
  return formatLocaleResult(normalizedLocale);
}

/**
 * This composable detects and resolves the current Locale, Country, and Language.
 *
 * It employs a **Chain of Responsibility** pattern to determine the locale, stopping at the first match:
 * 1. **DataScope/Query**: Checks headers (cookies/custom headers) or a specific `locale` query string.
 * 2. **URL/Host**: Analyzes the Host TLD (e.g., `.de` -> Germany) and URL path segments (e.g., `/nl_BE/`).
 * 3. **Window Global**: Checks for the legacy `window.mindShiftNav` property (Client-side fallback).
 *
 * If no locale is detected, it falls back to the default: **en-GB**.
 *
 * @param {HeadersObject} [headers] - Request headers. Essential for accurate detection during SSR.
 * @param {string} [localeQuery] - A specific locale string from the URL query params (e.g., "?locale=de-DE").
 *
 */
export function useLocale(headers?: HeadersObject, localeQuery?: string) {
  const locale = ref(DEFAULT_LOCALE.locale);
  const country = ref(DEFAULT_LOCALE.country);
  const language = ref(DEFAULT_LOCALE.language);

  const result = getLocaleFromDataScope(headers, localeQuery) ?? getLocaleFromUrl(headers) ?? getLocaleFromWindow();

  if (result) {
    locale.value = result.locale;
    country.value = result.country;
    language.value = result.language;
  } else {
    console.warn(`[useLocale] Unable to detect locale from context. Falling back to default: ${DEFAULT_LOCALE.locale}`);
  }

  return {
    locale,
    country,
    language,
  };
}
